import { useRef, useState } from "react";
import api, { imgUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, FileText } from "lucide-react";

export function DocumentUploader({ value = [], onChange, maxFiles = 5, testId = "document-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const docs = value || [];
  const atLimit = docs.length >= maxFiles;

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const room = maxFiles - docs.length;
    if (room <= 0) { toast.error(`You can upload up to ${maxFiles} documents`); return; }
    setUploading(true);
    const uploaded = [];
    for (const f of list.slice(0, room)) {
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/upload", form);
        uploaded.push(data.url);
        onChange([...docs, ...uploaded]);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (uploaded.length) toast.success(`${uploaded.length} document(s) uploaded`);
  };

  const removeAt = (idx) => onChange(docs.filter((_, i) => i !== idx));
  const fileName = (src) => src.split("/").pop() || "Document";

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
        multiple
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="space-y-2">
        {docs.map((src, idx) => (
          <div key={`${src}-${idx}`} className="flex items-center gap-3 border border-[#EBEBEB] rounded-xl p-2.5 bg-white">
            <FileText className="w-5 h-5 text-[#FF385C] shrink-0" />
            <a href={imgUrl(src)} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-view-${idx}`} className="flex-1 text-sm text-[#222] truncate hover:underline">
              {fileName(src)}
            </a>
            <button type="button" data-testid={`${testId}-remove-${idx}`} onClick={() => removeAt(idx)} className="text-[#717171] hover:text-[#C13515] p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {!atLimit && (
          <button
            type="button"
            data-testid={`${testId}-trigger`}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="w-full rounded-xl border-2 border-dashed border-[#DDDDDD] flex items-center justify-center gap-2 py-3 text-[#717171] hover:border-[#FF385C] hover:text-[#FF385C] transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span className="text-sm font-medium">{uploading ? "Uploading..." : "Upload ID (PDF or image)"}</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default DocumentUploader;
